import cx from 'classnames';
import { Col, Row } from 'antd';
import { useTranslation } from 'react-i18next';
import { useEffect, useRef, useState } from 'react';
import { RouteComponentProps } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';

import AppHeader from '../AppHeader/AppHeader';
import { Store } from '../../state/store.interface';
import { analysisTypes, THEME } from '../../config/commons';
import Content from '../../common/components/Content/Content';
import DotSpinner from '../../common/components/DotSpinner/DotSpinner';

import './Detail.scss';
import Map from './Map';
import Sample from './Sample';
import Histogram from './Histogram';
import DataDetail from './DataDetail';
import styles from './Detail.module.scss';
import GrowOutSizesChart from './GrowOutSizesChart';
import { getTitleCountAnimalChart } from './helpers';
import { fetchAnalysis } from './detailAnalysisSlice';
import HorizontalBarsChart from './HorizontalBarsChart';
import { handleResizeEvent } from '../../utils/dimensions';
import { LrvTabs } from '../../common/components/LrvTabs/LrvTabs';
import { getSampleTitle, HorizontalChartTypes, isConsolidatedAnalysis } from './helpers';

type TParams = {
  id: string,
  token: string,
};

function DetailAnalysis ({ match }: RouteComponentProps<TParams>) {
  const [t] = useTranslation();
  const dispatch = useDispatch();

  const chartContainerRef = useRef<HTMLDivElement>(null);
  const [chartWidth, setChartWidth] = useState(0);
  const [chartHeight, setChartHeight] = useState(0);

  const { allAnalysis, analysis: analysisOriginal, isLoading, currentIndex, theme } = useSelector((state: Store) => state.detail);
  const currentAnalysis = allAnalysis ? allAnalysis[currentIndex] : analysisOriginal;

  const analysisId = match.params.id;
  const token = match.params.token;
  const isLightTheme = theme === THEME.LIGHT;

  useEffect(() => {
    const lrvTabsContainer = document.querySelector('.lrv-tabs-container .ant-tabs-content');
    const width = lrvTabsContainer?.clientWidth || 0;
    const height = lrvTabsContainer?.clientHeight || 0;
    setChartWidth(width);
    setChartHeight(height);
  }, [chartContainerRef.current]); // Initialize width and height when chartContainer was rendered

  useEffect(() => {
    handleResizeEvent(() => {
      const lrvTabsContainer = document.querySelector('.lrv-tabs-container .ant-tabs-content');
      const width = lrvTabsContainer?.clientWidth || 0;
      const height = lrvTabsContainer?.clientHeight || 0;
      setChartWidth(width);
      setChartHeight(height);
    });
  }, []);

  useEffect(() => {
    dispatch(fetchAnalysis({ analysisId, token }));
  }, [dispatch, analysisId, token]);

  if (isLoading) {
    return (
      <div className={styles.analysisSpinner}>
        <DotSpinner theme={theme} />
      </div>
    );
  }

  if (analysisOriginal._id === '') {
    return (
      <div className={styles.analysisSpinner}>
        <DotSpinner theme={theme} />
      </div>
    );
  }

  const renderBarsCharts = () => {
    const animalPerGroup = getTitleCountAnimalChart(analysisOriginal.phaseType);

    if (analysisOriginal.type === analysisTypes.CONSOLIDATED_ADULT_ANALYSIS && !!analysisOriginal.growOutSizes?.whole?.labels) {
      return (
        <div ref={chartContainerRef} className={styles.postlarvaePerGroup}>
          <Content className='lrv-tabs-container' title={t('detail.chart.commercialSizes')} noPadding theme={theme}>
            <LrvTabs
              className='commercialSizesTabs'
              theme={theme}
              items={
                [
                  {
                    key: '1',
                    label: t('detail.growOutSizes.whole'),
                    children: <GrowOutSizesChart
                      containerClassName={styles.containerBarsChart}
                      textClassName={styles.chartTextBarsChart}
                      textInsideBarClassName={styles.chartTextFillWhite}
                      analysisData={analysisOriginal}
                      typeChart='GROW_OUT_WHOLE'
                      width={chartWidth}
                      height={chartHeight}
                      theme={theme}
                    />

                  },
                  {
                    key: '2',
                    label: t('detail.growOutSizes.tail'),
                    children: <GrowOutSizesChart
                      containerClassName={styles.containerBarsChart}
                      textClassName={styles.chartTextBarsChart}
                      textInsideBarClassName={styles.chartTextFillWhite}
                      analysisData={analysisOriginal}
                      typeChart='GROW_OUT_TAIL'
                      width={chartWidth}
                      height={chartHeight}
                      theme={theme}
                    />
                  },
                  {
                    key: '3',
                    label: t('detail.chart.weightGroups'),
                    children: <div className={styles.weightGroups}>
                      <Content
                        headerClassName={styles.headerChart}
                        titleClassName={isLightTheme ? styles.boldTitle : ''}
                        title={animalPerGroup}
                        noPadding
                        theme={theme}
                      >
                        <HorizontalBarsChart
                          width={chartWidth}
                          height={(chartHeight / 2) - 40}
                          analysisData={analysisOriginal}
                          typeChart={HorizontalChartTypes.SHRIMPS_PER_GROUP}
                          theme={theme}
                        />
                      </Content>
                      <Content
                        headerClassName={styles.headerChart}
                        titleClassName={isLightTheme ? styles.boldTitle : ''}
                        title={t('detail.chart.weightGroup')}
                        noPadding
                        theme={theme}
                      >
                        <HorizontalBarsChart
                          width={chartWidth}
                          height={(chartHeight / 2) - 40}
                          analysisData={analysisOriginal}
                          typeChart={HorizontalChartTypes.CUMMULATIVE_WEIGHT_PER_GROUP}
                          theme={theme}
                        />
                      </Content>
                    </div>
                  }
                ]
              }
            >
            </LrvTabs>
          </Content>
        </div>
      );
    }

    return (
      <>
        <div className={styles.postlarvaePerGroup}>
          <Content
            titleClassName={isLightTheme ? styles.boldTitle : ''}
            title={animalPerGroup}
            noPadding
            theme={theme}
          >
            <HorizontalBarsChart
              analysisData={analysisOriginal}
              typeChart={HorizontalChartTypes.SHRIMPS_PER_GROUP}
              theme={theme}
            />
          </Content>
        </div>
        <div className={styles.weightPerGroup}>
          <Content
            titleClassName={isLightTheme ? styles.boldTitle : ''}
            title={t('detail.chart.weightGroup')}
            noPadding
            theme={theme}
          >
            <HorizontalBarsChart
              analysisData={analysisOriginal}
              typeChart={HorizontalChartTypes.CUMMULATIVE_WEIGHT_PER_GROUP}
              theme={theme}
            />
          </Content>
        </div>
      </>
    );
  };

  return (
    <>
      <AppHeader />
      <div
        className={cx(styles.mainContent, isLightTheme ? styles.mainContentLight : styles.mainContentDark)}
      >
        <div className={cx(styles.detailAnalysis, 'detailAnalysis')}>
          <Row className={styles.body}>
            <Col xs={24} sm={24} md={24} lg={12} xl={8} xxl={8} className={cx(styles.histogramColumn, 'histogramColumn')}>
              <div className={styles.histogram} >
                <Content title={t('detail.histogram')} titleClassName={styles.title} noPadding theme={theme}>
                  <Histogram />
                </Content>
              </div>

              <div className={styles.detail} >
                <DataDetail />
              </div>
            </Col>

            <Col xs={24} sm={24} md={24} lg={24} xl={8} xxl={8} className={styles.sampleColumn}>
              <div className={styles.sample}>
                <Content titleClassName={isLightTheme ? styles.boldTitle : ''} title={getSampleTitle(isConsolidatedAnalysis(analysisOriginal.type) ? currentAnalysis?.code : '')} noPadding theme={theme}>
                  <Sample />
                </Content>
              </div>
              <div className={styles.map}>
                <Content titleClassName={isLightTheme ? styles.boldTitle : ''} title={t('detail.map')} noPadding theme={theme}>
                  <Map />
                </Content>
              </div>
            </Col>

            <Col xs={24} sm={24} md={24} lg={12} xl={8} xxl={8} className={styles.barsChartColumn}>
              {renderBarsCharts()}
            </Col>
          </Row>
        </div>
      </div>
    </>
  );
}

export default DetailAnalysis;
